import { useCallback } from "react";
import React from "react";
import { Button } from "@zapier/design-system";
import { ToolComponentProps } from "@zapier/ai-toolkit/client";
import { useAtomValue } from "jotai";
import { currentAccountIdAtom } from "@zapier/canvas-state";
import { useCreateAppConnection } from "../../shared/auth";
import styles from "./AuthConfirmationButton.module.css";
import {
  AuthConfirmationButtonParamsSchema,
  AuthConfirmationButtonResult,
} from "./index";

export function AuthConfirmationButton(
  props: ToolComponentProps<
    typeof AuthConfirmationButtonParamsSchema,
    AuthConfirmationButtonResult
  >,
) {
  const { args } = props;

  // Early return before any hooks to avoid hooks rules violation
  if (!args) {
    return null;
  }

  const { scenario, app_name, auth_details, suggested_auth } = args;

  const accountId = useAtomValue(currentAccountIdAtom);
  const { createNewConnection } = useCreateAppConnection(Number(accountId));

  const sendMessage = useCallback((message: string, source: string) => {
    window.postMessage(
      {
        type: "COPILOT_USER_MESSAGE",
        payload: {
          message,
          source,
          messageType: "text",
        },
      },
      "*",
    );
  }, []);

  const handleUseAuth = useCallback(() => {
    if (scenario === "single_auth") {
      sendMessage("Use this authentication", "auth-confirmation-single");
    } else {
      sendMessage("use suggested", "auth-confirmation-suggested");
    }
  }, [scenario, sendMessage]);

  const handleCreateNew = useCallback(async () => {
    if (!app_name) return;

    try {
      // Use the same auth creation logic as the create_auth tool
      // First fetch app metadata to get the right format for createNewConnection
      const searchTerm = app_name
        .replace(/CLIAPI$/, "")
        .replace(/API$/, "")
        .toLowerCase();

      const res = await fetch(
        `/api/v4/implementations-meta/search?term=${searchTerm}`,
      );
      const data = await res.json();

      if (data.results && data.results.length > 0) {
        const matchingApp = data.results[0];
        const fullAppData = {
          id: app_name,
          name: matchingApp.app_name || app_name,
          authType: matchingApp.auth_type || "oauth",
          hasUpfrontFields: matchingApp.has_upfront_fields || false,
          ...matchingApp,
        };

        // Create the connection using the same logic as CreateAuth component
        await createNewConnection(fullAppData);

        // Send success message to chat
        sendMessage(
          `✅ Authentication popup opened for ${fullAppData.name}. Please complete the authentication process.`,
          "auth-creation-triggered",
        );
      } else {
        throw new Error(`No app metadata found for ${app_name}`);
      }
    } catch (error) {
      console.error("Failed to create auth:", error);
      // Fallback to requesting the AI to create auth
      sendMessage(
        `Please create authentication for ${app_name}`,
        "auth-creation-fallback",
      );
    }
  }, [app_name, createNewConnection, sendMessage]);

  const handleSelectDifferent = useCallback(() => {
    sendMessage("select different", "auth-confirmation-select");
  }, [sendMessage]);

  if (scenario === "single_auth") {
    return (
      <div className={styles.container}>
        <div className={styles.description}>
          {auth_details ? (
            <>
              Found authentication: <strong>{auth_details.auth_label}</strong>{" "}
              (Status: {auth_details.status})
            </>
          ) : (
            <>Found authentication for {app_name}</>
          )}
        </div>
        <div className={styles.buttonGroup}>
          <Button variant="primary" size="medium" onPress={handleUseAuth}>
            Use this authentication
          </Button>
          <Button variant="secondary" size="medium" onPress={handleCreateNew}>
            Create new
          </Button>
        </div>
      </div>
    );
  }

  if (scenario === "multiple_auth") {
    return (
      <div className={styles.container}>
        <div className={styles.description}>
          Multiple authentications found for {app_name}
          {suggested_auth && (
            <>
              <br />
              Suggested: <strong>
                {suggested_auth.auth_label}
              </strong> (Status: {suggested_auth.status})
            </>
          )}
        </div>
        <div className={styles.buttonGroupWrap}>
          {suggested_auth && (
            <Button variant="primary" size="medium" onPress={handleUseAuth}>
              Use suggested
            </Button>
          )}
          <Button
            variant="secondary"
            size="medium"
            onPress={handleSelectDifferent}
          >
            Select different
          </Button>
          <Button variant="secondary" size="medium" onPress={handleCreateNew}>
            Create new
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
